import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FacturasCliente } from './LiquidacionVales';
import ReactDOM from 'react-dom';

// Componente auxiliar para el portal
const FacturaPortal = ({ children, isOpen, targetRef, onClose }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const portalRef = useRef(null);

  useEffect(() => {
    if (isOpen && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen, targetRef]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (portalRef.current && 
          !portalRef.current.contains(event.target) && 
          !targetRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, targetRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      ref={portalRef}
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        zIndex: 99999,
        backgroundColor: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        borderRadius: '8px',
        border: '1px solid #ddd',
        maxHeight: '300px',
        overflowY: 'auto'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
};

// Definir constantes
const motivosDevolucion = {
  1: { texto: "Cambio de Precio", color: "#FF9800", icon: "💰" },
  3: { texto: "Dañado", color: "#F44336", icon: "⚠️" },
  4: { texto: "Devolución Cliente", color: "#9C27B0", icon: "👤" },
  5: { texto: "Exceso Inventario", color: "#3F51B5", icon: "📦" },
  6: { texto: "Fecha Vencimiento", color: "#2196F3", icon: "📅" },
  9: { texto: "Vencido", color: "#607D8B", icon: "⏰" },
  11: { texto: "Otro", color: "#795548", icon: "❓" }
};

const estadosDevolucion = {
  1: 'Creado',
  2: 'Autorizado',
  3: 'Asignado',
  4: 'Recolectado',
  5: 'No Recolectado',
  6: 'Recibido',
  7: 'Liquidado',
  8: 'SAP'
};

const ModalLiquidacion = ({ devolucion, onClose, onConfirmarLiquidacion }) => {
  const { user } = useSelector(state => state.auth);
  const [cargando, setCargando] = useState(false);
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState({});
  const [todasFacturasSeleccionadas, setTodasFacturasSeleccionadas] = useState(false);
  const [dropdownActivo, setDropdownActivo] = useState(null);
  const inputRefs = useRef({});

  // Inicializar facturas seleccionadas
  useEffect(() => {
    if (devolucion && devolucion.DEV_DET) {
      const facturasIniciales = {};
      devolucion.DEV_DET.forEach(producto => {
        facturasIniciales[producto.ID_Devolucion_Detalle] = null;
        inputRefs.current[producto.ID_Devolucion_Detalle] = React.createRef();
      });
      setFacturasSeleccionadas(facturasIniciales);
    }
  }, [devolucion]);

  // Verificar si todas las facturas están seleccionadas
  useEffect(() => {
    const todasSeleccionadas = Object.values(facturasSeleccionadas).every(factura => factura !== null);
    setTodasFacturasSeleccionadas(todasSeleccionadas);
  }, [facturasSeleccionadas]);

  const handleFacturaSeleccion = (productoId, factura) => {
    setFacturasSeleccionadas(prev => ({
      ...prev,
      [productoId]: factura
    }));
  };

  const handleConfirmar = async () => {
    setCargando(true);
    try {
      // Preparar datos para liquidación
      const datosLiquidacion = {
        ID_Vale: devolucion.ID_Vale,
        ID_Devolucion: devolucion.ID_Devolucion,
        facturas: facturasSeleccionadas,
        usuario: user.id_usuario || 'Sistema',
        fechaLiquidacion: new Date().toISOString()
      };

      console.log('Datos de liquidación a enviar:', datosLiquidacion);
      await onConfirmarLiquidacion(datosLiquidacion);

    } catch (error) {
      console.error('Error al confirmar liquidación:', error);
    } finally {
      setCargando(false);
    }
  };

  const toggleDropdown = (productoId) => {
    setDropdownActivo(dropdownActivo === productoId ? null : productoId);
  };

  if (!devolucion) return null;

  const totalUnidades = devolucion.DEV_DET.reduce((sum, p) => sum + p.Cantidad, 0);
  const facturasCompletadas = Object.values(facturasSeleccionadas).filter(f => f !== null).length;
  const totalProductos = devolucion.DEV_DET.length;
  const porcentajeCompletado = totalProductos > 0 ? Math.round((facturasCompletadas / totalProductos) * 100) : 0;

  return (
    <div className="modal-overlay modal-liquidacion-overlay">
      <div className="modal-content-liquidacion modal-liquidacion-content">
        {/* Header */}
        <div className="modal-header modal-liquidacion-header">
          <div className="header-content">
            <div className="header-icon-title">
              <span className="header-icon">💰</span>
              <div>
                <h2>Confirmar Liquidación de Vale</h2>
                <p className="header-subtitle">Proceso final para cerrar la devolución</p>
              </div>
            </div>
            <div className="header-badges">
              <span className="badge-vale">Vale: <strong>{devolucion.ID_Vale}</strong></span>
              <span className="badge-estado">
                Estado: <strong>{estadosDevolucion[devolucion.Id_Rastreo] || 'Creado'}</strong>
              </span>
            </div>
          </div>
          <button className="btn-cerrar-modal" onClick={onClose}>×</button>
        </div>

        <div className="modal-body modal-liquidacion-body">
          {/* Información General */}
          <div className="seccion-info-general modal-info-section">
            <div className="section-title">
              <span className="title-icon">📋</span>
              <h3>Información del Vale</h3>
            </div>
            <div className="info-grid-modal modal-info-grid">
              <div className="info-card">
                <div className="info-card-icon">🏷️</div>
                <div className="info-card-content">
                  <div className="info-card-label">Vale</div>
                  <div className="info-card-value destacado">{devolucion.ID_Vale}</div>
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-icon">👤</div>
                <div className="info-card-content">
                  <div className="info-card-label">Cliente</div>
                  <div className="info-card-value">{devolucion.Cliente}</div>
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-icon">📍</div>
                <div className="info-card-content">
                  <div className="info-card-label">Ruta</div>
                  <div className="info-card-value">{devolucion.Ruta}</div>
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-icon">📅</div>
                <div className="info-card-content">
                  <div className="info-card-label">Fecha</div>
                  <div className="info-card-value">{new Date(devolucion.Fecha_Ruta).toLocaleDateString()}</div>
                </div>
              </div>
            </div>

            {/* Resumen de Progreso */}
            <div className="progreso-resumen">
              <div className="progreso-card">
                <div className="progreso-header">
                  <span className="progreso-icon">📊</span>
                  <span className="progreso-label">Progreso de Facturas</span>
                </div>
                <div className="progreso-bar-container">
                  <div 
                    className="progreso-bar-fill"
                    style={{ width: `${porcentajeCompletado}%` }}
                  ></div>
                </div>
                <div className="progreso-stats">
                  <span className="progreso-text">
                    {facturasCompletadas} de {totalProductos} facturas seleccionadas
                  </span>
                  <span className="progreso-porcentaje">{porcentajeCompletado}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Productos con Facturas */}
          <div className="seccion-productos-liquidacion modal-productos-section">
            <div className="section-title">
              <span className="title-icon">📦</span>
              <h3>Productos para Liquidación</h3>
              <span className="title-badge">{totalProductos} productos • {totalUnidades} unidades</span>
            </div>

            <div className="productos-table-container">
              <div className="table-scroll-wrapper">
                <table className="productos-table">
                  <thead>
                    <tr>
                      <th className="col-codigo">Código</th>
                      <th className="col-nombre">Producto</th>
                      <th className="col-cantidad">Cantidad</th>
                      <th className="col-lote">Lote</th>
                      <th className="col-motivo">Motivo</th>
                      <th className="col-factura">Factura Asociada</th>
                      <th className="col-estado">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devolucion.DEV_DET.map((producto) => {
                      const facturaSeleccionada = facturasSeleccionadas[producto.ID_Devolucion_Detalle];
                      const motivo = motivosDevolucion[producto.ID_Motivo_Devolucion];
                      const isDropdownActivo = dropdownActivo === producto.ID_Devolucion_Detalle;
                      
                      return (
                        <tr key={producto.ID_Devolucion_Detalle} className="producto-row">
                          {/* Código */}
                          <td className="col-codigo">
                            <span className="codigo-producto">
                              {producto.ID_Producto}
                            </span>
                          </td>
                          
                          {/* Nombre */}
                          <td className="col-nombre">
                            <div className="nombre-producto" title={producto.ItemName}>
                              {producto.ItemName}
                            </div>
                          </td>
                          
                          {/* Cantidad */}
                          <td className="col-cantidad">
                            <span className="cantidad-badge">
                              {producto.Cantidad}
                            </span>
                          </td>
                          
                          {/* Lote */}
                          <td className="col-lote">
                            <span className="lote-texto">
                              {producto.Lote || "N/A"}
                            </span>
                          </td>
                          
                          {/* Motivo */}
                          <td className="col-motivo">
                            <div 
                              className="badge-motivo-con-icono"
                              style={{
                                backgroundColor: `${motivo?.color}15`,
                                borderLeft: `4px solid ${motivo?.color}`
                              }}
                            >
                              <span className="motivo-icon">{motivo?.icon || "❓"}</span>
                              <span className="motivo-texto">
                                {motivo?.texto || `Motivo ${producto.ID_Motivo_Devolucion}`}
                              </span>
                            </div>
                          </td>
                          
                          {/* Factura */}
                          <td className="col-factura">
                            <div className="factura-container">
                              {facturaSeleccionada ? (
                                <div className="factura-seleccionada-card">
                                  <div className="factura-header">
                                    <span className="factura-icon">📄</span>
                                    <span className="factura-numero">
                                      {facturaSeleccionada.NumAtCard || 'Sin número'}
                                    </span>
                                    <button 
                                      className="btn-cambiar-factura"
                                      onClick={() => handleFacturaSeleccion(producto.ID_Devolucion_Detalle, null)}
                                      title="Cambiar factura"
                                    >
                                      🔄
                                    </button>
                                  </div>
                                  <div className="factura-detalles">
                                    <span className="factura-detalle">Fecha: {facturaSeleccionada.DocDate || 'N/A'}</span>
                                    <span className="factura-detalle">Cant: {facturaSeleccionada.Quantity || 'N/A'}</span>
                                    {facturaSeleccionada.DocNum && (
                                      <span className="factura-detalle">Doc: {facturaSeleccionada.DocNum}</span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div 
                                    className="selector-factura"
                                    ref={inputRefs.current[producto.ID_Devolucion_Detalle]}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDropdown(producto.ID_Devolucion_Detalle);
                                    }}
                                  >
                                    <input
                                      type="text"
                                      placeholder="🔍 Buscar factura..."
                                      readOnly
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        backgroundColor: 'white',
                                        fontSize: '14px'
                                      }}
                                    />
                                  </div>
                                  
                                  <FacturaPortal
                                    isOpen={isDropdownActivo}
                                    targetRef={inputRefs.current[producto.ID_Devolucion_Detalle]}
                                    onClose={() => setDropdownActivo(null)}
                                  >
                                    <div style={{ padding: '12px' }}>
                                      <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                                        Buscar factura para: {producto.ItemName}
                                      </div>
                                      <FacturasCliente
                                        CardCode={devolucion.ID_Cliente}
                                        ItemCode={producto.ID_Producto}
                                        user={user}
                                        placeholder="Escribe número de factura..."
                                        onSeleccionar={(factura) => {
                                          handleFacturaSeleccion(producto.ID_Devolucion_Detalle, factura);
                                          setDropdownActivo(null);
                                        }}
                                        style={{
                                          width: '100%'
                                        }}
                                      />
                                    </div>
                                  </FacturaPortal>
                                </>
                              )}
                            </div>
                          </td>
                          
                          {/* Estado */}
                          <td className="col-estado">
                            <div className="estado-factura">
                              {facturaSeleccionada ? (
                                <span className="badge-completado">
                                  <span className="badge-icon">✅</span>
                                  <span className="badge-texto">Completado</span>
                                </span>
                              ) : (
                                <span className="badge-pendiente">
                                  <span className="badge-icon">⏳</span>
                                  <span className="badge-texto">Pendiente</span>
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Resumen de Totales */}
            <div className="resumen-totales">
              <div className="totales-grid">
                <div className="total-card">
                  <div className="total-icon">📦</div>
                  <div className="total-content">
                    <div className="total-label">Total Productos</div>
                    <div className="total-value">{totalProductos} items</div>
                  </div>
                </div>
                <div className="total-card">
                  <div className="total-icon">📊</div>
                  <div className="total-content">
                    <div className="total-label">Total Unidades</div>
                    <div className="total-value">{totalUnidades} unidades</div>
                  </div>
                </div>
                <div className="total-card">
                  <div className="total-icon">✅</div>
                  <div className="total-content">
                    <div className="total-label">Facturas Completadas</div>
                    <div className="total-value">
                      {facturasCompletadas} / {totalProductos}
                    </div>
                  </div>
                </div>
                <div className="total-card progreso">
                  <div className="total-icon">📈</div>
                  <div className="total-content">
                    <div className="total-label">Progreso</div>
                    <div className="total-value">{porcentajeCompletado}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mensaje de Confirmación */}
          <div className="confirmacion-section">
            <div className="confirmacion-card">
              <div className="confirmacion-icon">⚠️</div>
              <div className="confirmacion-content">
                <h4>Confirmación Requerida</h4>
                <p className="confirmacion-texto">
                  Esta acción cambiará el estado del vale a <strong>"Liquidado"</strong> 
                  y registrará todas las facturas asociadas. Esta operación no se podrá revertir.
                </p>
                {!todasFacturasSeleccionadas && (
                  <div className="advertencia-faltantes">
                    <span className="advertencia-icon">ℹ️</span>
                    <span className="advertencia-texto">
                      Debes seleccionar una factura para cada producto antes de proceder con la liquidación.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer modal-liquidacion-footer">
          <div className="footer-left">
            <span className="estado-preparacion">
              {todasFacturasSeleccionadas ? (
                <span className="preparado">
                  <span className="estado-icon">✅</span>
                  Listo para liquidar
                </span>
              ) : (
                <span className="incompleto">
                  <span className="estado-icon">⏳</span>
                  {facturasCompletadas} de {totalProductos} facturas seleccionadas
                </span>
              )}
            </span>
          </div>
          <div className="footer-right">
            <button 
              className="btn-cancelar" 
              onClick={onClose} 
              disabled={cargando}
            >
              <span className="btn-icon">✖️</span>
              Cancelar
            </button>
            <button
              className="btn-confirmar-liquidacion"
              onClick={handleConfirmar}
              disabled={cargando || !todasFacturasSeleccionadas}
            >
              {cargando ? (
                <>
                  <span className="btn-spinner"></span>
                  Procesando Liquidación...
                </>
              ) : (
                <>
                  <span className="btn-icon">💰</span>
                  Confirmar Liquidación
                  {todasFacturasSeleccionadas && <span className="btn-subtext">Listo</span>}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalLiquidacion;